import { useState } from "react";
import { useThemeColor } from "@/hooks/useThemeColor";

interface ImageGalleryProps {
  images: string[];
  name: string;
}

export default function ImageGallery({ images, name }: ImageGalleryProps) {
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const { primary, hexAlpha } = useThemeColor();

  const prev = () => setActive((p) => (p === 0 ? images.length - 1 : p - 1));
  const next = () => setActive((p) => (p === images.length - 1 ? 0 : p + 1));

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Main Image */}
      <div
        className="relative w-full h-[420px] md:h-[540px] rounded-2xl overflow-hidden bg-[#111] group cursor-zoom-in"
        style={{
          border: `1px solid ${hexAlpha(primary, 0.15)}`,
          boxShadow: `0 0 60px ${hexAlpha(primary, 0.06)}, inset 0 0 40px rgba(0,0,0,0.4)`,
        }}
        onClick={() => setZoomed(true)}
      >
        <img
          src={images[active]}
          alt={`${name} - صورة ${active + 1}`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-transparent pointer-events-none"></div>

        {/* Corner accents */}
        <div className="absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 rounded-tr-2xl pointer-events-none" style={{ borderColor: hexAlpha(primary, 0.3) }}></div>
        <div className="absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 rounded-bl-2xl pointer-events-none" style={{ borderColor: hexAlpha(primary, 0.3) }}></div>

        {/* Nav arrows */}
        <button
          onClick={(e) => { e.stopPropagation(); prev(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/70 backdrop-blur-sm text-white rounded-full transition-all cursor-pointer opacity-0 group-hover:opacity-100 border border-white/10"
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = primary; (e.currentTarget as HTMLElement).style.color = "#000"; (e.currentTarget as HTMLElement).style.borderColor = primary; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(0,0,0,0.7)"; (e.currentTarget as HTMLElement).style.color = "#fff"; (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)"; }}
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); next(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/70 backdrop-blur-sm text-white rounded-full transition-all cursor-pointer opacity-0 group-hover:opacity-100 border border-white/10"
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = primary; (e.currentTarget as HTMLElement).style.color = "#000"; (e.currentTarget as HTMLElement).style.borderColor = primary; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(0,0,0,0.7)"; (e.currentTarget as HTMLElement).style.color = "#fff"; (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)"; }}
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>

        {/* Counter */}
        <div
          className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-xs px-3 py-1.5 rounded-full"
          style={{ fontFamily: "'Tajawal', sans-serif", color: primary, border: `1px solid ${hexAlpha(primary, 0.2)}` }}
        >
          {active + 1} / {images.length}
        </div>

        {/* Zoom icon */}
        <div className="absolute top-4 left-4 w-9 h-9 flex items-center justify-center bg-black/70 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-all" style={{ color: primary, border: `1px solid ${hexAlpha(primary, 0.2)}` }}>
          <i className="ri-zoom-in-line text-sm"></i>
        </div>
      </div>

      {/* Thumbnails */}
      <div className="flex gap-3 justify-center flex-wrap">
        {images.map((img, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className="relative w-20 h-16 md:w-24 md:h-20 rounded-xl overflow-hidden border-2 transition-all duration-300 cursor-pointer flex-shrink-0"
            style={{
              borderColor: active === i ? primary : "rgba(255,255,255,0.1)",
              opacity: active === i ? 1 : 0.5,
              transform: active === i ? "scale(1.05)" : "scale(1)",
              boxShadow: active === i ? `0 0 16px ${hexAlpha(primary, 0.3)}` : "none",
            }}
            onMouseEnter={(e) => { if (active !== i) { (e.currentTarget as HTMLElement).style.opacity = "0.8"; (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.4); } }}
            onMouseLeave={(e) => { if (active !== i) { (e.currentTarget as HTMLElement).style.opacity = "0.5"; (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)"; } }}
          >
            <img src={img} alt={`thumb ${i + 1}`} className="w-full h-full object-cover object-top" />
            {active === i && (
              <div className="absolute inset-0" style={{ background: hexAlpha(primary, 0.1) }}></div>
            )}
          </button>
        ))}
      </div>

      {/* Zoom Modal */}
      {zoomed && (
        <div
          className="fixed inset-0 z-50 bg-black/97 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setZoomed(false)}
        >
          <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at center, ${hexAlpha(primary, 0.04)} 0%, transparent 70%)` }}></div>

          <button
            className="absolute top-6 left-6 w-11 h-11 flex items-center justify-center bg-white/5 border border-white/10 text-white rounded-full transition-all cursor-pointer"
            onClick={() => setZoomed(false)}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = primary; (e.currentTarget as HTMLElement).style.color = "#000"; (e.currentTarget as HTMLElement).style.borderColor = primary; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.05)"; (e.currentTarget as HTMLElement).style.color = "#fff"; (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)"; }}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <button
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/5 border border-white/10 text-white rounded-full transition-all cursor-pointer"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = primary; (e.currentTarget as HTMLElement).style.color = "#000"; (e.currentTarget as HTMLElement).style.borderColor = primary; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.05)"; (e.currentTarget as HTMLElement).style.color = "#fff"; (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)"; }}
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
          <button
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/5 border border-white/10 text-white rounded-full transition-all cursor-pointer"
            onClick={(e) => { e.stopPropagation(); next(); }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = primary; (e.currentTarget as HTMLElement).style.color = "#000"; (e.currentTarget as HTMLElement).style.borderColor = primary; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.05)"; (e.currentTarget as HTMLElement).style.color = "#fff"; (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)"; }}
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <img
            src={images[active]}
            alt={name}
            className="max-w-full max-h-[90vh] object-contain rounded-2xl"
            style={{ border: `1px solid ${hexAlpha(primary, 0.1)}`, boxShadow: `0 0 80px ${hexAlpha(primary, 0.1)}` }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
